import { createActionHelpers } from 'vuex-loading';
import Unleash from '@js/plugins/unleash';

const { startLoading, endLoading } = createActionHelpers({
    moduleName: 'loading',
});

const state = {
    transaction: {
        id: null,
        receipt_number: null,
        discounts: [],
        forecourt_attendant: {
            id: null,
        },
        outlet: {
            company_details: {},
        },
        line_items: [
            {
                item_type: null,
                applied_discounts: [],
            },
        ],
    },
    cancelledItemContext: false,
};

const getters = {
    pgsql_transaction: state => state.transaction,

    receiptType: state => state.transaction.receipt_type,

    isSale: (state, getters) => getters.receiptType === 'SALE',

    isVoid: (state, getters) => getters.receiptType === 'VOID',

    isRefund: (state, getters) => getters.receiptType === 'REFUND',

    outlet: state => state.transaction.outlet,

    isShellSGReceipt: () => Unleash.hasFeature('shell_sg_receipt'),

    discountById: state => {
        return state.transaction.discounts.reduce((acc, discount) => {
            acc[discount.id] = discount;
            return acc;
        }, {});
    },

    receipt: state => {
        const orig = state.transaction.original_receipt;
        return {
            receipt_number: state.transaction.id,
            sequence_number: state.transaction.receipt_number,
            original_receipt: orig
                ? {
                      receipt_number: orig.receipt_id,
                      sequence_number: orig.receipt_number,
                  }
                : null,
        };
    },

    discountLineItems: (state, getters) => {
        const taxShortNameById = getters.taxShortNameById;
        const discountById = getters.discountById;

        let discountLineItems = state.transaction.line_items.filter(item => {
            if (item.cancelled) return false;

            if (getters.isShellSGReceipt && item.applied_discounts.length > 0) {
                // return false if item has manual discount
                return !isHasManualDiscount(
                    item.applied_discounts[0].discount_id,
                    discountById,
                );
            }

            return item.applied_discounts.length;
        });

        if (discountLineItems.length) {
            let di = state.transaction.discounts;

            discountLineItems.map(item => {
                item.applied_discounts.map(appliedDiscount => {
                    let discount = di.find(
                        d => d.id === appliedDiscount.discount_id,
                    );

                    if (discount) {
                        appliedDiscount.discount = discount;
                    }
                });

                item.taxShortName = getTaxShortName(
                    item.applied_taxes,
                    taxShortNameById,
                );
            });
        }

        discountLineItems.sort((a, b) => {
            if (a.item_type > b.item_type) {
                return -1;
            }

            return 0;
        });

        return discountLineItems;
    },

    multipleDiscountLineItems: state => {
        let discountLineItems = state.transaction.line_items.filter(item => {
            return (
                item.applied_discounts.length > 1 &&
                item.applied_discounts.every(
                    discount => discount.applied_amount !== 0,
                )
            );
        });

        return discountLineItems.length;
    },

    taxShortNameById: state => {
        return Object.fromEntries(
            (state.transaction.taxes || []).map(tax => [
                tax.id,
                tax.short_name,
            ]),
        );
    },

    groupDiscounts: (state, getters) => {
        const discountById = getters.discountById;
        const discountLineItems = getters.discountLineItems;
        const isShellSGReceipt = getters.isShellSGReceipt;

        // 1. Always filter out MANUAL discounts.
        //    Filter out SHELL_CAPILLARY only for Shell SG receipts.
        const discounts = state.transaction.discounts.filter(discount => {
            if (discount.discount_source === 'MANUAL_DISCOUNT') return false;
            if (
                isShellSGReceipt &&
                discount.discount_source === 'SHELL_CAPILLARY'
            )
                return false;
            return true;
        });

        // 2. Group discounts by promotion_id
        const groupedDiscounts = _.groupBy(discounts, 'promotion_id');

        // 3. Process each group
        const mapGroupDiscounts = Object.values(groupedDiscounts).map(group => {
            const first = group[0];

            const promotionId = first.promotion_id;
            const discountName = first.name;
            const discountSource = first.discount_source;

            const discountQuantity = group.reduce((sum, d) => {
                return sum + parseFloat(d.quantity || 0);
            }, 0);

            const relatedLineItems = [];

            discountLineItems.forEach(lineItem => {
                (lineItem.applied_discounts || []).forEach(appliedDiscount => {
                    const discount = appliedDiscount.discount || {};
                    if (
                        discount.promotion_id === promotionId &&
                        appliedDiscount.applied_amount !== 0
                    ) {
                        relatedLineItems.push(lineItem);
                    }
                });
            });

            // Group related items by product_id
            const groupedLineItems = _.groupBy(relatedLineItems, 'product_id');

            const mapGroupLineItems = Object.values(groupedLineItems).map(
                lineItems => {
                    const firstItem = lineItems[0];

                    const productName = firstItem.name;
                    const unitPrice = firstItem.unit_price;
                    const itemType = firstItem.item_type;
                    const fuelDetails = firstItem.fuel_details;
                    const taxShortName = firstItem.taxShortName;

                    const sumByKey = (items, key) =>
                        items.reduce(
                            (sum, item) => sum + parseFloat(item[key] || 0),
                            0,
                        );

                    const quantity = sumByKey(lineItems, 'quantity');
                    const netAmount = sumByKey(lineItems, 'net_amount');
                    const amount = sumByKey(lineItems, 'amount');
                    let totalAmount = sumByKey(lineItems, 'total_amount');
                    let hasManualDiscount = false;
                    let totalDiscountPerItem = 0;
                    let totalManualDiscountPerItem = 0;
                    let manualDiscountName = '';

                    // Total discount from the first item's applied_discounts
                    let totalDiscount = 0;
                    if (
                        firstItem.applied_discounts &&
                        firstItem.applied_discounts.length > 0
                    ) {
                        totalDiscount = firstItem.applied_discounts.reduce(
                            (sum, discount) => {
                                return (
                                    sum +
                                    parseFloat(discount.applied_amount || 0)
                                );
                            },
                            0,
                        );
                    }

                    // Shell SG property
                    if (isShellSGReceipt) {
                        totalDiscountPerItem = lineItems.reduce((sum, item) => {
                            const appliedDiscounts =
                                item.applied_discounts || [];
                            const validDiscounts = appliedDiscounts.filter(
                                d => {
                                    const discountInfo =
                                        discountById[d.discount_id];
                                    return (
                                        d.applied_amount !== 0 &&
                                        discountInfo &&
                                        ![
                                            'SHELL_CAPILLARY',
                                            'MANUAL_DISCOUNT',
                                        ].includes(discountInfo.discount_source)
                                    );
                                },
                            );

                            const subtotal = validDiscounts.reduce((s, d) => {
                                return (
                                    s +
                                    parseFloat(
                                        (d.discount && d.applied_amount) || 0,
                                    )
                                );
                            }, 0);

                            return sum + subtotal;
                        }, 0);

                        // manually calculate the total amount since it's should only reflect the current applied discount/exlude capillary
                        totalAmount = amount + totalDiscountPerItem;

                        // check if item has manual discount
                        hasManualDiscount = lineItems[0].applied_discounts.some(
                            discount => {
                                let selectedDiscount =
                                    discountById[discount.discount_id];

                                // if manual discount found, add it to the line item properties
                                if (
                                    selectedDiscount &&
                                    selectedDiscount.discount_source ===
                                        'MANUAL_DISCOUNT'
                                ) {
                                    totalManualDiscountPerItem =
                                        discount.applied_amount;
                                    totalAmount += totalManualDiscountPerItem;
                                    manualDiscountName = selectedDiscount.name;

                                    return true;
                                }

                                return false;
                            },
                        );
                    }

                    return {
                        productName,
                        unitPrice,
                        quantity,
                        netAmount,
                        totalDiscount,
                        totalAmount,
                        amount,
                        itemType,
                        fuelDetails,
                        totalDiscountPerItem,
                        taxShortName,
                        hasManualDiscount,
                        manualDiscountName,
                        totalManualDiscountPerItem,
                    };
                },
            );

            return {
                discountName,
                discountQuantity,
                promotionId,
                discountSource,
                lineItems: relatedLineItems,
                groupLineItems: mapGroupLineItems,
            };
        });

        return mapGroupDiscounts;
    },

    fuels: (state, getters) => {
        const { isShellSGReceipt, taxShortNameById, discountById } = getters;

        if (!isShellSGReceipt) {
            return state.transaction.line_items.filter(lineItem => {
                if (lineItem.item_type !== 'fuel' || lineItem.cancelled)
                    return false;
                return !lineItem.applied_discounts.length;
            });
        }

        return state.transaction.line_items
            .filter(lineItem => {
                if (lineItem.item_type !== 'fuel' || lineItem.cancelled)
                    return false;

                // No discounts applied at all
                if (
                    !lineItem.applied_discounts ||
                    lineItem.applied_discounts.length === 0
                ) {
                    return true;
                }

                const discountId = lineItem.applied_discounts[0]
                    ? lineItem.applied_discounts[0].discount_id
                    : '';
                const areCapillaryDiscounts = areAllCapillaryDiscounts(
                    lineItem.applied_discounts.map(
                        discount => discount.discount_id,
                    ),
                    discountById,
                );
                return (
                    isHasManualDiscount(discountId, discountById) ||
                    areCapillaryDiscounts
                );
            })
            .map(lineItem => {
                let hasManualDiscount = false;

                if (lineItem.applied_discounts.length > 0) {
                    const discountId =
                        lineItem.applied_discounts[0].discount_id;

                    hasManualDiscount = isHasManualDiscount(
                        discountId,
                        discountById,
                    );

                    // attach full discount object to the line item
                    if (hasManualDiscount) {
                        lineItem.applied_discounts[0].discount =
                            discountById[discountId] || {};
                    }
                }

                let totalDiscountPerItem = lineItem.applied_discounts
                    .filter(
                        discount =>
                            discount && parseFloat(discount.applied_amount) > 0,
                    )
                    .map(discount => parseFloat(discount.applied_amount))
                    .reduce((a, b) => a + b, 0);

                return {
                    ...lineItem,
                    totalAmount: lineItem.total_amount,
                    taxShortName: getTaxShortName(
                        lineItem.applied_taxes,
                        taxShortNameById,
                    ),
                    hasManualDiscount,
                    totalDiscountPerItem,
                };
            });
    },

    products: (state, getters) => {
        const { transaction } = state;
        const { isShellSGReceipt, taxShortNameById, discountById } = getters;

        if (!isShellSGReceipt) {
            return transaction.line_items.filter(function(item) {
                const discounts = item.applied_discounts || [];
                const totalDiscount = discounts.reduce((sum, d) => {
                    return sum + parseFloat(d.applied_amount || 0);
                }, 0);

                return (
                    item.item_type === 'cstore' &&
                    !item.cancelled &&
                    (discounts.length === 0 ||
                        totalDiscount === 0 ||
                        transaction.receipt_type === 'REFUND')
                );
            });
        }

        const filteredItems = transaction.line_items.filter(function(item) {
            if (item.item_type !== 'cstore' || item.cancelled) {
                return false;
            }

            const discounts = item.applied_discounts || [];

            // push discount object into applied_discounts, used by exporter
            item.applied_discounts.map(function(discount) {
                if (
                    discount.discount_id &&
                    discountById[discount.discount_id]
                ) {
                    discount.discount = discountById[discount.discount_id];
                }
            });

            if (
                discounts.length === 0 ||
                transaction.receipt_type === 'REFUND'
            ) {
                return true;
            }

            const discountId = discounts[0].discount_id;
            const totalDiscount = discounts.reduce(function(sum, discount) {
                return sum + parseFloat(discount.applied_amount || 0);
            }, 0);

            return (
                isHasManualDiscount(discountId, discountById) ||
                totalDiscount === 0 ||
                areAllCapillaryDiscounts(
                    item.applied_discounts.map(
                        discount => discount.discount_id,
                    ),
                    discountById,
                )
            );
        });

        const groupedItems = _.groupBy(filteredItems, 'product_id');

        function sum(items, key) {
            return items.reduce(function(total, item) {
                return total + parseFloat(item[key] || 0);
            }, 0);
        }

        return Object.values(groupedItems).map(function(lineItems) {
            const first = lineItems[0];

            const productName = first.name;
            const unitPrice = first.unit_price;
            const itemType = first.item_type;
            const fuelDetails = first.fuel_details;
            const appliedDiscounts = first.applied_discounts || [];
            const appliedTaxes = first.applied_taxes;

            const quantity = sum(lineItems, 'quantity');
            const netAmount = sum(lineItems, 'net_amount');
            const amount = sum(lineItems, 'amount');

            const totalDiscount = appliedDiscounts.reduce(function(sum, d) {
                return sum + parseFloat(d.applied_amount || 0);
            }, 0);

            // Shell SG refund: hub-core embeds charges (e.g. BCRS) in total_amount;
            // strip them out so "Price After Discount" excludes charges shown separately.
            const totalCharges = lineItems.reduce(function(s, item) {
                return (
                    s +
                    (item.applied_charges || []).reduce(function(cs, c) {
                        return cs + parseFloat(c.applied_amount || 0);
                    }, 0)
                );
            }, 0);
            const totalAmount =
                isShellSGReceipt && transaction.receipt_type === 'REFUND'
                    ? sum(lineItems, 'total_amount') - totalCharges
                    : sum(lineItems, 'total_amount');

            const discountId =
                appliedDiscounts.length > 0
                    ? appliedDiscounts[0].discount_id
                    : '';
            const hasManualDiscount =
                discountId && isHasManualDiscount(discountId, discountById);
            const discount = hasManualDiscount ? discountById[discountId] : {};

            const taxShortName = getTaxShortName(
                appliedTaxes,
                taxShortNameById,
            );

            return {
                productName,
                unitPrice,
                itemType,
                fuelDetails,
                quantity,
                netAmount,
                totalDiscount,
                totalAmount,
                amount,
                taxShortName,
                hasManualDiscount,
                discount,
            };
        });
    },

    cancelProducts: (state, getters) => {
        const { taxShortNameById } = getters;

        const cancelledItems = state.transaction.line_items.filter(lineItem => {
            return lineItem.item_type === 'cstore' && lineItem.cancelled;
        });

        return cancelledItems.map(lineItem => {
            const taxShortName = getTaxShortName(
                lineItem.applied_taxes,
                taxShortNameById,
            );

            return {
                ...lineItem,
                productName: lineItem.name,
                unitPrice: lineItem.unit_price,
                quantity: lineItem.quantity,
                amount: lineItem.amount,
                taxShortName,
            };
        });
    },

    totalSaving: state => {
        return Math.abs(
            state.transaction.discounts
                .map(d => {
                    return parseFloat(d.applied_amount);
                })
                .reduce((a, b) => a + b, 0),
        ).toFixed(2);
    },

    change: state => {
        let change = 0;

        let cash = state.transaction.payments.find(p => p.type === 0);

        if (cash) {
            change = cash.change ? cash.change : 0;
        }

        return change.toFixed(2);
    },

    paymentName: state => {
        return state.transaction.payments.map(p => p.name.toLowerCase());
    },

    overrun: state => {
        let overrunFuel = state.transaction.line_items.find(lineItem => {
            return (
                lineItem.item_type === 'fuel' &&
                lineItem.fuel_details &&
                lineItem.fuel_details.overrun &&
                lineItem.fuel_details.overrun.amount > 0
            );
        });

        return overrunFuel ? overrunFuel.fuel_details.overrun.amount : 0;
    },

    approvalCode: state => {
        let edc = state.transaction.payments.find(p => p.source === 'edc');

        if (edc) {
            return edc.card.approval_code;
        }

        let shellApp = state.transaction.payments.find(
            p => p.source === 'shell_app',
        );

        if (shellApp) {
            return shellApp.details.approvalCode;
        }

        let tngRfid = state.transaction.payments.find(
            p => p.source === 'tng_rfid',
        );

        if (tngRfid) {
            return tngRfid.details.approvalCode;
        }

        return null;
    },

    stan: state => {
        let shellApp = state.transaction.payments.find(
            p => p.source === 'shell_app',
        );

        if (shellApp) {
            return shellApp.details.stan;
        }

        return null;
    },

    referenceNo: state => {
        let eps = state.transaction.payments.find(
            p => p.source === 'eps_invenco',
        );

        if (eps && eps.details) {
            return eps.details.reference_no;
        }

        let ewallet = state.transaction.payments.find(
            p => p.source === 'ewallet',
        );

        if (ewallet) {
            return ewallet.ewallet.reference_no;
        }

        let subsidy = state.transaction.payments.find(
            p => p.source === 'subsidy',
        );

        if (subsidy) {
            return subsidy.subsidy.reference_no;
        }

        return null;
    },

    ghlPaymentReferenceNo: state => {
        let ghl = state.transaction.payments.find(p => p.source === 'ghl');

        if (ghl) {
            return ghl.details.OriginalRetTxnRef
                ? ghl.details.OriginalRetTxnRef
                : ghl.details.RetTxnRef;
        }

        return null;
    },

    ghlMerchantId: state => {
        let ghl = state.transaction.payments.find(p => p.source === 'ghl');

        if (ghl) {
            return ghl.details.MerchantID ? ghl.details.MerchantID : null;
        }

        return null;
    },

    ghlTerminalId: state => {
        let ghl = state.transaction.payments.find(p => p.source === 'ghl');

        if (ghl) {
            return ghl.details.TerminalID ? ghl.details.TerminalID : null;
        }

        return null;
    },

    ghlRefundReferenceNo: state => {
        let ghl = state.transaction.payments.find(p => p.source === 'ghl');

        if (ghl) {
            return ghl.details.OriginalRetTxnRef == null
                ? null
                : ghl.details.RetTxnRef;
        }
    },

    loyaltyDetails: state => {
        return state.transaction.loyalty;
    },

    eftDetails: state => {
        return state.transaction.payments.find(p => p.source === 'eps_invenco');
    },

    groupTaxes: (state, getters) => {
        let taxes = state.transaction.taxes;

        let groupTaxes = _.groupBy(taxes, 'tax_id');

        let mapGroupTaxes = [];

        _.map(groupTaxes, taxes => {
            let taxId = taxes[0].tax_id;
            let taxName = taxes[0].name;

            let taxTotal = taxes
                .map(taxes => {
                    return parseFloat(taxes.applied_amount);
                })
                .reduce((a, b) => a + b, 0);

            mapGroupTaxes.push({
                taxId,
                taxName,
                taxTotal,
            });
        });

        return mapGroupTaxes;
    },

    capillaryDiscounts: state => {
        let capillaryDiscounts = state.transaction.discounts.filter(
            discount => {
                return discount.discount_source === 'SHELL_CAPILLARY';
            },
        );

        let groupCapillaryDiscounts = _.groupBy(capillaryDiscounts, 'name');

        let mapGroupCapillaryDiscounts = [];

        _.map(groupCapillaryDiscounts, discounts => {
            let discountName = discounts[0].name;

            let discountAppliedAmount = discounts
                .map(discount => {
                    return parseFloat(discount.applied_amount);
                })
                .reduce((a, b) => a + b, 0);

            mapGroupCapillaryDiscounts.push({
                discountName,
                discountAppliedAmount,
            });
        });

        return mapGroupCapillaryDiscounts;
    },

    totalDiscounts: state => {
        return state.transaction.discounts
            .filter(discount => {
                return discount.discount_source != 'SHELL_CAPILLARY';
            })
            .map(discount => {
                return parseFloat(discount.applied_amount);
            })
            .reduce((a, b) => a + b, 0);
    },

    // Shell SG: BCRS charges come as one entry per eligible line item;
    // aggregate by charge_id so the BOS receipt shows one row per charge type.
    aggregatedCharges: state => {
        return _.map(
            _.groupBy(state.transaction.charges || [], 'charge_id'),
            function(charges) {
                return {
                    name: charges[0].name,
                    applied_amount: charges.reduce(function(s, c) {
                        return s + parseFloat(c.applied_amount || 0);
                    }, 0),
                };
            },
        );
    },

    taxSummaries: state => {
        const result = Object.entries(
            _.groupBy(state.transaction.taxes, 'short_name'),
        ).map(function([taxId, group]) {
            let taxableLineItemIds = [];
            let taxableSales = 0;
            let processedLineItemIds = {};

            for (let i = 0; i < state.transaction.line_items.length; i++) {
                let item = state.transaction.line_items[i];

                if (item.cancelled) continue;

                if (
                    item.applied_taxes &&
                    Array.isArray(item.applied_taxes) &&
                    !processedLineItemIds[item.id]
                ) {
                    for (let j = 0; j < item.applied_taxes.length; j++) {
                        let tax = item.applied_taxes[j];

                        for (let k = 0; k < group.length; k++) {
                            if (tax.tax_id === group[k].id) {
                                taxableLineItemIds.push(item.id);
                                taxableSales += parseFloat(item.net_amount);
                                processedLineItemIds[item.id] = true;
                                break;
                            }
                        }
                    }
                }
            }

            let totalTaxAmount = 0;
            for (let g = 0; g < group.length; g++) {
                totalTaxAmount += group[g].applied_amount;
            }

            return {
                shortName: group[0].short_name,
                rate: parseFloat(group[0].percentage),
                taxableSales: parseFloat(taxableSales.toFixed(2)),
                amount: parseFloat(totalTaxAmount.toFixed(2)),
            };
        });

        return result;
    },

    paymentEftDetails: state => {
        return state.transaction.payments.find(
            p =>
                p.details &&
                p.details.receipt &&
                Array.isArray(p.details.receipt.receiptLines),
        );
    },

    metaData: state => {
        return state.transaction.meta_data;
    },

    cancelledItemContext: state => state.cancelledItemContext,

    contextualSubtotal: (state, getters) => {
        if (getters.cancelledItemContext) {
            return getters.cancelProducts.reduce((sum, product) => {
                return sum + parseFloat(product.amount || 0);
            }, 0);
        }
        return state.transaction.sub_total;
    },

    contextualGrandTotal: (state, getters) => {
        if (getters.cancelledItemContext) {
            return getters.contextualSubtotal;
        }
        return state.transaction.grand_total;
    },

    hasManualDiscount: state => {
        return state.transaction.discounts.some(discount => {
            return (
                discount.discount_source == 'MANUAL_DISCOUNT' &&
                discount.applied_amount !== 0
            );
        });
    },
};

const mutations = {
    setTransaction(state, data) {
        state.transaction = data;
    },

    setCancelledItemContext(state, value) {
        state.cancelledItemContext = value;
    },
};

const actions = {
    async getTransactionPostgres({ dispatch, commit }, receiptNumber) {
        startLoading(dispatch, 'fetch-transaction');

        let url = Laroute.route(
            'franchise.report.transaction.getTransactionReceiptPostgres',
            { receiptNumber },
        );

        try {
            const { data } = await axios.get(url);
            endLoading(dispatch, 'fetch-transaction');

            commit('setTransaction', data);

            return data;
        } catch (e) {
            endLoading(dispatch, 'fetch-transaction');
            throw e;
        }
    },
};

function getTaxShortName(appliedTaxes = [], taxShortNameById = {}) {
    if (appliedTaxes.length > 0) {
        return taxShortNameById[appliedTaxes[0].tax_id] || '';
    }
    return '';
}

function isHasManualDiscount(discountId = '', discounts = {}) {
    let selectedDiscount = discounts[discountId];

    return (
        selectedDiscount &&
        selectedDiscount.discount_source === 'MANUAL_DISCOUNT'
    );
}

function areAllCapillaryDiscounts(discountIds = '', discounts = {}) {
    const ids = Array.isArray(discountIds) ? discountIds : [discountIds];

    return ids.every(id => {
        const discount = discounts[id];
        return discount && discount.discount_source === 'SHELL_CAPILLARY';
    });
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
